import React from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileTab } from "@/components/profile/tabs/ProfileTab";
import { SecurityTab } from "@/components/profile/tabs/SecurityTab";
import { NotificationsTab } from "@/components/profile/tabs/NotificationsTab";
import { ServiceProviderTab } from "@/components/profile/tabs/ServiceProviderTab";
import { PeopleTab } from "@/components/profile/tabs/PeopleTab";
import { SettingsTab } from "@/components/profile/tabs/SettingsTab";

/**
 * ProfilePage - Main profile settings page with tabbed navigation
 *
 * Features:
 * - 6 tabs: Profile, Security, Notifications, Service Provider, People, Settings
 * - URL-synced tab state via query params (?tab=security)
 * - Responsive layout (tabs stack on mobile)
 * - Protected route (requires authentication)
 * - Follows TerraMatrix design system
 *
 * Navigation:
 * - /profile → Profile tab (default)
 * - /profile?tab=security → Security tab (includes activity/audit logs)
 * - /profile?tab=notifications → Notifications tab
 * - /profile?tab=service-provider → Service Provider tab
 * - /profile?tab=people → People tab (sponsorship management)
 * - /profile?tab=settings → Settings tab (workspace preferences)
 */
export const ProfilePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current tab from URL, default to 'profile'
  const currentTab = searchParams.get("tab") || "profile";

  // Handle tab change - update URL query params
  const handleTabChange = (value: string) => {
    if (value === "profile") {
      // Remove tab param for default tab to keep URL clean
      searchParams.delete("tab");
      setSearchParams(searchParams);
    } else {
      searchParams.set("tab", value);
      setSearchParams(searchParams);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">Profile Settings</h1>
        <p className="text-muted-foreground">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Tabbed Interface */}
      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 h-auto p-1 bg-muted rounded-lg">
          <TabsTrigger
            value="profile"
            className="py-2.5 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
            data-testid="profile-tab-trigger"
          >
            Profile
          </TabsTrigger>
          <TabsTrigger
            value="security"
            className="py-2.5 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
            data-testid="security-tab-trigger"
          >
            Security
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="py-2.5 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
            data-testid="notifications-tab-trigger"
          >
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="service-provider"
            className="py-2.5 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
            data-testid="service-provider-tab-trigger"
          >
            Service Provider
          </TabsTrigger>
          <TabsTrigger
            value="people"
            className="py-2.5 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
            data-testid="people-tab-trigger"
          >
            People
          </TabsTrigger>
          <TabsTrigger
            value="settings"
            className="py-2.5 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
            data-testid="settings-tab-trigger"
          >
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab Content */}
        <TabsContent
          value="profile"
          className="mt-6"
          data-testid="profile-tab-content"
        >
          <ProfileTab />
        </TabsContent>

        {/* Security Tab Content */}
        <TabsContent
          value="security"
          className="mt-6"
          data-testid="security-tab-content"
        >
          <SecurityTab />
        </TabsContent>

        {/* Notifications Tab Content */}
        <TabsContent
          value="notifications"
          className="mt-6"
          data-testid="notifications-tab-content"
        >
          <NotificationsTab />
        </TabsContent>

        {/* Service Provider Tab Content */}
        <TabsContent
          value="service-provider"
          className="mt-6"
          data-testid="service-provider-tab-content"
        >
          <ServiceProviderTab />
        </TabsContent>

        {/* People Tab Content */}
        <TabsContent
          value="people"
          className="mt-6"
          data-testid="people-tab-content"
        >
          <PeopleTab />
        </TabsContent>

        {/* Settings Tab Content */}
        <TabsContent
          value="settings"
          className="mt-6"
          data-testid="settings-tab-content"
        >
          <SettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
